import { uniq } from "@-/com.npmjs.array-utils-ts";
import { DatabaseRecoveryRequiredError } from "#database-corruption-error";

/** A held ownership lease; native binds the OS lock to this object's lifetime. */
export interface DatabaseGenerationLease {
  release(): void;
}

/** File sets are immutable in location. Only this manifest changes their roles. */
export interface DatabaseGenerationStorage {
  acquire(name: string): DatabaseGenerationLease;
  allocate(): string;
  compareAndSwap(previous: string | null, next: string): void;
  exists(path: string): boolean;
  /** Every allocated generation directory, as `.haniverse-generations/<id>`. */
  listGenerations(): string[];
  read(): string | null;
  /** Delete one file, or one directory recursively. An absent path is not an error. */
  remove(path: string): void;
}

interface GenerationSlot {
  active: string | null;
  quarantined?: boolean;
  replace: boolean;
}
interface GenerationManifest {
  slots: Record<string, GenerationSlot>;
  version: 1;
}

const NAME = /^[a-z0-9-]+\.db$/;
const GENERATION = /^\.haniverse-generations\/[a-zA-Z0-9-]+\/actor\.db$/;
const SIDECARS = ["", "-wal", "-shm", "-journal", "-changes", "-wal-revert", "-info"];
// Legacy in-place resets left this marker beside the file set it guarded.
const LEGACY_LEFTOVERS = [...SIDECARS, "-reset-pending"];

export class DatabaseGenerations {
  constructor(private readonly storage: DatabaseGenerationStorage) {}

  private read(): { manifest: GenerationManifest; text: string | null } {
    const text = this.storage.read();
    if (text === null) {
      return { manifest: { slots: {}, version: 1 }, text };
    }
    const value: unknown = JSON.parse(text);
    if (typeof value !== "object" || value === null || Reflect.get(value, "version") !== 1) {
      throw new Error("Invalid database generation manifest; all files preserved.");
    }
    const slots: unknown = Reflect.get(value, "slots");
    if (typeof slots !== "object" || slots === null || Array.isArray(slots)) {
      throw new Error("Invalid database generation slots; all files preserved.");
    }
    const validated: Record<string, GenerationSlot> = {};
    for (const [name, slot] of Object.entries(slots)) {
      if (typeof slot !== "object" || slot === null || !NAME.test(name)) {
        throw new Error("Invalid database generation slot; all files preserved.");
      }
      const active: unknown = Reflect.get(slot, "active");
      const replace: unknown = Reflect.get(slot, "replace");
      if (
        typeof replace !== "boolean" ||
        !(
          active === null ||
          (typeof active === "string" && (NAME.test(active) || GENERATION.test(active)))
        )
      ) {
        throw new Error("Invalid database generation path; all files preserved.");
      }
      const quarantined: unknown = Reflect.get(slot, "quarantined");
      if (quarantined !== undefined && typeof quarantined !== "boolean") {
        throw new Error("Invalid quarantine state");
      }
      validated[name] = { active, replace, ...(quarantined === true ? { quarantined: true } : {}) };
    }
    const activePaths: string[] = [];
    for (const slot of Object.values(validated)) {
      if (slot.active === null || slot.replace) {
        continue;
      }
      if (activePaths.includes(slot.active)) {
        throw new Error(
          "Multiple logical databases reference the same active generation; recovery is required.",
        );
      }
      activePaths.push(slot.active);
    }
    return { manifest: { slots: validated, version: 1 }, text };
  }

  private slot(manifest: GenerationManifest, name: string): GenerationSlot {
    if (!NAME.test(name)) {
      throw new Error("Invalid logical database name");
    }
    const recorded = manifest.slots[name];
    if (recorded !== undefined) {
      return recorded;
    }
    // Import legacy files IN PLACE. Never copy/rename an individual live SQLite file.
    if (this.storage.exists(`${name}-reset-pending`)) {
      throw new Error("Legacy database reset is incomplete; recovery is required.");
    }
    return {
      active: SIDECARS.some((suffix) => this.storage.exists(name + suffix)) ? name : null,
      replace: false,
    };
  }

  peek(name: string): string | null {
    const slot = this.slot(this.read().manifest, name);
    return slot.replace ? null : slot.active;
  }

  /** The lease covers connect, all queries, sync, and the final native close. */
  claim(name: string) {
    const lease = this.storage.acquire(name);
    try {
      const { manifest } = this.read();
      const slot = this.slot(manifest, name);
      // Typed, so the app can offer the reset instead of treating it as a transient failure.
      if (slot.quarantined && !slot.replace) {
        throw new DatabaseRecoveryRequiredError(new Error("Study database is quarantined"));
      }
      const reusable = slot.replace ? null : slot.active;
      const fresh = reusable === null;
      const path = reusable ?? this.storage.allocate();
      // A fresh candidate may be bootstrapped from the cloud, and the engine writes that
      // image over whatever main file it finds while keeping any WAL beside it. That is
      // exactly how sign-up paired an empty cloud database with a guest WAL on 2026-09-20.
      if (fresh && SIDECARS.some((suffix) => this.storage.exists(path + suffix))) {
        throw new Error("A new database generation must start empty; existing files preserved.");
      }
      if (!fresh && !this.storage.exists(path)) {
        throw new Error(
          "The active database main file is missing; recovery is required. Sidecars preserved.",
        );
      }
      let released = false;
      let published = false;
      return {
        fresh,
        path,
        publish: () => {
          if (released) {
            throw new Error("Database generation lease has ended");
          }
          if (published) {
            return;
          }
          const current = this.read();
          const actual = this.slot(current.manifest, name);
          if (actual.active !== slot.active || actual.replace !== slot.replace) {
            throw new Error("Database generation changed while opening; files preserved.");
          }
          current.manifest.slots[name] = { active: path, replace: false };
          this.storage.compareAndSwap(current.text, JSON.stringify(current.manifest));
          published = true;
        },
        quarantine: () => {
          if (released) {
            throw new Error("Database generation lease has ended");
          }
          if (fresh && !published) {
            return;
          } // Failed candidates never become active.
          const current = this.read();
          current.manifest.slots[name] = {
            ...this.slot(current.manifest, name),
            quarantined: true,
          };
          this.storage.compareAndSwap(current.text, JSON.stringify(current.manifest));
        },
        release: () => {
          if (released) {
            return;
          }
          lease.release();
          released = true;
        },
      };
    } catch (error) {
      lease.release();
      throw error;
    }
  }

  /** Request a fresh generation. Existing bytes survive even if the next open fails. */
  retire(names: readonly string[]): void {
    this.withClosed(names, () => {
      const current = this.read();
      for (const name of names) {
        current.manifest.slots[name] = { ...this.slot(current.manifest, name), replace: true };
      }
      this.storage.compareAndSwap(current.text, JSON.stringify(current.manifest));
    });
  }

  /**
   * Clean-install reset: each named database next opens EMPTY (never a cloud bootstrap over
   * leftovers), and its files — active, retired or quarantined — are deleted.
   *
   * Unreferencing is one atomic manifest swap; deletion afterwards is only garbage
   * collection, so an interrupted purge leaves inert files, never a half-deleted live set.
   * When every other slot is closed too, orphans from earlier retirements and failed
   * candidates are swept as well. An open slot blocks only that sweep: its in-flight
   * candidate directory is unreferenced until publish and must not be collected.
   * Returns the deleted paths.
   */
  purge(names: readonly string[]): string[] {
    return this.withClosed(names, () => {
      const current = this.read();
      // Not `slot()`: an unrecorded name IS its legacy file set (in `names` below), and a
      // leftover reset marker must be cleaned up here rather than block the reset.
      const superseded = names.flatMap((name) => current.manifest.slots[name]?.active ?? []);
      for (const name of names) {
        current.manifest.slots[name] = { active: null, replace: false };
      }
      this.storage.compareAndSwap(current.text, JSON.stringify(current.manifest));
      const referenced = new Set(
        Object.values(current.manifest.slots).flatMap((slot) =>
          slot.active === null || slot.replace ? [] : [slot.active],
        ),
      );
      const others = Object.keys(current.manifest.slots).filter((name) => !names.includes(name));
      const sweep = this.tryWithClosed(others, () => [
        ...this.storage.listGenerations().map((directory) => `${directory}/actor.db`),
        ...Object.keys(current.manifest.slots),
      ]);
      const removed: string[] = [];
      for (const path of uniq([...superseded, ...names, ...(sweep ?? [])])) {
        if (!referenced.has(path)) {
          removed.push(...this.removeFileSet(path));
        }
      }
      return removed;
    });
  }

  /** A generation is its directory; a legacy set is its main file plus every sidecar. */
  private removeFileSet(path: string): string[] {
    if (GENERATION.test(path)) {
      const directory = path.slice(0, -"/actor.db".length);
      this.storage.remove(directory);
      return [directory];
    }
    const removed = LEGACY_LEFTOVERS.map((suffix) => path + suffix).filter((file) =>
      this.storage.exists(file),
    );
    for (const file of removed) {
      this.storage.remove(file);
    }
    return removed;
  }

  /** Atomic role transfer, including all sidecars because no physical file moves. */
  handoff(from: string, to: string): boolean {
    return this.withClosed([from, to], () => {
      const current = this.read();
      const source = this.slot(current.manifest, from);
      const target = this.slot(current.manifest, to);
      if (source.quarantined) {
        throw new Error("Quarantined study data requires verified recovery");
      }
      if (source.active === null || source.replace) {
        return false;
      }
      if (target.active !== null && !target.replace) {
        throw new Error("A guest handoff already exists; both databases were preserved.");
      }
      current.manifest.slots[to] = { active: source.active, replace: false };
      current.manifest.slots[from] = { active: null, replace: false };
      this.storage.compareAndSwap(current.text, JSON.stringify(current.manifest));
      return true;
    });
  }

  /** Like `withClosed`, but an owned (open) database skips the operation instead of throwing. */
  private tryWithClosed<T>(names: readonly string[], operation: () => T): T | undefined {
    const leases: DatabaseGenerationLease[] = [];
    try {
      for (const name of names) {
        try {
          leases.push(this.storage.acquire(name));
        } catch {
          return undefined;
        }
      }
      return operation();
    } finally {
      for (const lease of leases.reverse()) {
        lease.release();
      }
    }
  }

  private withClosed<T>(names: readonly string[], operation: () => T): T {
    const leases: DatabaseGenerationLease[] = [];
    try {
      for (const name of names) {
        leases.push(this.storage.acquire(name));
      }
      return operation();
    } finally {
      for (const lease of leases.reverse()) {
        lease.release();
      }
    }
  }
}
